/**
 * Queries and mutations of the user access front door (`/access/*`, 5 Oct).
 *
 * Send is one POST that files several Ivanti requests, so it carries one
 * `Idempotency-Key` per attempt (the server derives one per item from it):
 * sending again after a timeout replays what was filed and retries only what
 * failed. A new attempt — the answers changed — gets a new key.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AccessApproversPreview,
  type AccessApproversPreviewRequest,
  type AccessAuditResponse,
  type AccessCase,
  type AccessCaseActivity,
  type AccessCaseListResponse,
  type AccessCaseSubmission,
  type AccessCaseSubmitResponse,
  type AccessCatalogResponse,
  type AccessDecisionRequest,
  type AccessDecisionResponse,
  type AccessDuplicateCheck,
  type AccessFiledItem,
  type AccessItemSubmission,
  type AccessMyCasesResponse,
  type AccessPrefillRequest,
  type AccessPrefillResponse,
  type AccessQueuesResponse,
  type AccessReferenceRolesResponse,
  type AccessRejectionSeenResponse,
  type AccessResourceOwner,
  type AccessType,
  acknowledgeAccessRejection as sdkAcknowledgeAccessRejection,
  addAccessCaseComment as sdkAddAccessCaseComment,
  checkAccessDuplicate as sdkCheckAccessDuplicate,
  decideAccessApproval as sdkDecideAccessApproval,
  fileAccessItem as sdkFileAccessItem,
  getAccessCase as sdkGetAccessCase,
  getAccessCatalog as sdkGetAccessCatalog,
  getAccessItemPrefill as sdkGetAccessItemPrefill,
  getAccessPrefill as sdkGetAccessPrefill,
  getAccessQueues as sdkGetAccessQueues,
  getAccessReferenceAccess as sdkGetAccessReferenceAccess,
  getAccessReferenceProfile as sdkGetAccessReferenceProfile,
  getAccessReferenceRoles as sdkGetAccessReferenceRoles,
  getAccessResourceOwner as sdkGetAccessResourceOwner,
  listAccessCaseActivity as sdkListAccessCaseActivity,
  listAccessCases as sdkListAccessCases,
  listAccessCompleted as sdkListAccessCompleted,
  type ListAccessCompletedData,
  listMyAccessCases as sdkListMyAccessCases,
  listMyApprovals as sdkListMyApprovals,
  type MyApprovalsResponse,
  previewAccessApprovers as sdkPreviewAccessApprovers,
  type ReferenceAccessResponse,
  type ReferenceProfileResponse,
  submitAccessCase as sdkSubmitAccessCase,
  type TicketTimelineEntry,
  useKiosk,
  useMe,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { type ApprovalsNav, approvalsNav } from "./lib/my-approvals";

export const accessKeys = {
  all: ["access"] as const,
  catalog: () => [...accessKeys.all, "catalog"] as const,
  prefill: (body: AccessPrefillRequest) => [...accessKeys.all, "prefill", body] as const,
  list: () => [...accessKeys.all, "list"] as const,
  mine: () => [...accessKeys.all, "mine"] as const,
  approvals: () => [...accessKeys.all, "approvals"] as const,
  detail: (caseId: string) => [...accessKeys.all, "detail", caseId] as const,
  activity: (caseId: string) => [...accessKeys.all, "activity", caseId] as const,
  itemPrefill: (caseId: string, n: number) =>
    [...accessKeys.all, "item-prefill", caseId, n] as const,
  completed: (query: AccessCompletedQuery) => [...accessKeys.all, "completed", query] as const,
  duplicate: (query: AccessDuplicateQuery) => [...accessKeys.all, "duplicate", query] as const,
  reference: (employeeId: string) => [...accessKeys.all, "reference", employeeId] as const,
  queues: () => [...accessKeys.all, "queues"] as const,
  referenceAccess: (employeeId: string, compareTo: string | null) =>
    [...accessKeys.all, "reference-access", employeeId, compareTo ?? ""] as const,
  referenceRoles: (employeeId: string) =>
    [...accessKeys.all, "reference-roles", employeeId] as const,
  resourceOwner: (item: string, resource: string) =>
    [...accessKeys.all, "resource-owner", item, resource] as const,
  approvers: (caseId: string, n: number, body: AccessApproversPreviewRequest) =>
    [...accessKeys.all, "approvers", caseId, n, body] as const,
};

/**
 * The reference user's access on record (5 Oct): the current picture per
 * system only. Read when HR picks the reference user; nothing while unpicked.
 */
export function useReferenceProfile(employeeId: string | null) {
  return useQuery({
    queryKey: accessKeys.reference(employeeId ?? ""),
    queryFn: async (): Promise<ReferenceProfileResponse> => {
      logger.debug("[access] reference profile", employeeId);
      const { data } = await sdkGetAccessReferenceProfile({
        query: { employee_id: employeeId ?? "" },
        throwOnError: true,
      });
      return data;
    },
    enabled: employeeId !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * The reference user's access per system (21 Oct), to keep, remove or add to:
 * every entry with its source and date. `compareTo` (Change): mark what that
 * person already has. Read only while `enabled` (the switch is on and a
 * reference user is picked); the server logs who asked about whom.
 */
export function useReferenceAccess(
  employeeId: string | null,
  enabled: boolean,
  compareTo: string | null = null,
) {
  return useQuery({
    queryKey: accessKeys.referenceAccess(employeeId ?? "", compareTo),
    queryFn: async (): Promise<ReferenceAccessResponse> => {
      logger.debug("[access] reference access", employeeId, compareTo);
      const { data } = await sdkGetAccessReferenceAccess({
        query: {
          employee_id: employeeId ?? "",
          ...(compareTo ? { compare_to: compareTo } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    enabled: enabled && employeeId !== null && employeeId !== "",
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Who approves a new hire's software or folder first (13 Oct), for "Check
 * and send": the owner listed for the title or path in Resource owners, or
 * null when nobody is (Global IT Helpdesk just sets it up with the account).
 */
export function useResourceOwner(item: string, resource: string, enabled: boolean) {
  return useQuery({
    queryKey: accessKeys.resourceOwner(item, resource),
    queryFn: async (): Promise<AccessResourceOwner> => {
      const { data } = await sdkGetAccessResourceOwner({
        query: { item, resource },
        throwOnError: true,
      });
      return data;
    },
    enabled: enabled && resource !== "",
    staleTime: 60_000,
    retry: false,
  });
}

/** Who the wizard is asking about: a new hire by name, else an employee by id. */
export interface AccessDuplicateQuery {
  type: AccessType;
  name?: string;
  person_id?: string;
}

/**
 * Is there an open case of this type for this person already (7 Oct)? Asked
 * on "Who is it for?" once the name has settled (the caller debounces) or a
 * person is picked; Send checks again. Says whether the reader may override
 * a match by name (IT, admins).
 */
export function useAccessDuplicate(query: AccessDuplicateQuery, enabled: boolean) {
  return useQuery({
    queryKey: accessKeys.duplicate(query),
    queryFn: async (): Promise<AccessDuplicateCheck> => {
      logger.debug("[access] duplicate check", query.type);
      const { data } = await sdkCheckAccessDuplicate({ query, throwOnError: true });
      return data;
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** The checklist as the signed-in person's catalog offers it. */
export function useAccessCatalog() {
  return useQuery({
    queryKey: accessKeys.catalog(),
    queryFn: async (): Promise<AccessCatalogResponse> => {
      const { data } = await sdkGetAccessCatalog({ throwOnError: true });
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

/** One item's form and its first answers; a read, sent as POST for its body. */
export function useAccessPrefill(body: AccessPrefillRequest) {
  return useQuery({
    queryKey: accessKeys.prefill(body),
    queryFn: async (): Promise<AccessPrefillResponse> => {
      logger.debug("[access] prefill", body.item, body.action);
      const { data } = await sdkGetAccessPrefill({ body, throwOnError: true });
      return data;
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Send. The caller keeps the key for this attempt and reuses it to retry. */
export function useSubmitAccessCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      idempotencyKey,
    }: {
      body: AccessCaseSubmission;
      idempotencyKey: string;
    }): Promise<AccessCaseSubmitResponse> => {
      logger.debug("[access] send", body.who.type, body.items.length);
      const { data } = await sdkSubmitAccessCase({
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accessKeys.list() });
    },
  });
}

/** The access cases the signed-in person may see. */
export function useAccessCases() {
  return useQuery({
    queryKey: accessKeys.list(),
    queryFn: async (): Promise<AccessCaseListResponse> => {
      const { data } = await sdkListAccessCases({ throwOnError: true });
      return data;
    },
    staleTime: 30_000,
  });
}

/**
 * The cases the signed-in person is involved in, as My Tickets rows (6 Oct):
 * one row per case, its requests grouped under it. Only while the front door
 * is on (`useMe().access_front_door`; the route is 404 otherwise). Under
 * `accessKeys.all`, so every access mutation refreshes it too.
 */
export function useMyAccessCases(enabled: boolean) {
  return useQuery({
    queryKey: accessKeys.mine(),
    queryFn: async (): Promise<AccessMyCasesResponse> => {
      const { data } = await sdkListMyAccessCases({ throwOnError: true });
      logger.debug("[access] my cases", data.items?.length ?? 0);
      return data;
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * The work queues in IT Admin → Access (19 Oct, target flow): Global IT's new
 * hires, Backoffice's "Roles to select" and "Ready to assign". Admins and IT
 * only (the server says 403 to anyone else; the tabs show on `access_audit`).
 */
export function useAccessQueues(enabled: boolean) {
  return useQuery({
    queryKey: accessKeys.queues(),
    queryFn: async (): Promise<AccessQueuesResponse> => {
      const { data } = await sdkGetAccessQueues({ throwOnError: true });
      logger.debug(
        "[access] queues",
        data.global_it?.length ?? 0,
        data.roles_to_select?.length ?? 0,
      );
      return data;
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * The reference user's actual system roles (19 Oct): "not_available" until
 * the HelpDesk may read 365FO. Read when Backoffice opens a row.
 */
export function useAccessReferenceRoles(employeeId: string | null) {
  return useQuery({
    queryKey: accessKeys.referenceRoles(employeeId ?? ""),
    queryFn: async (): Promise<AccessReferenceRolesResponse> => {
      const { data } = await sdkGetAccessReferenceRoles({
        query: { employee_id: employeeId ?? "" },
        throwOnError: true,
      });
      return data;
    },
    enabled: employeeId !== null && employeeId !== "",
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** The Completed tab's filter, as the query string carries it (6 Oct, decision 34). */
export type AccessCompletedQuery = NonNullable<ListAccessCompletedData["query"]>;

/**
 * Completed access cases for auditors: admins and IT only (the server says
 * 403 to anyone else; the tab is shown on `useMe().access_audit`).
 */
export function useAccessCompleted(query: AccessCompletedQuery, enabled: boolean) {
  return useQuery({
    queryKey: accessKeys.completed(query),
    queryFn: async (): Promise<AccessAuditResponse> => {
      const { data } = await sdkListAccessCompleted({ query, throwOnError: true });
      return data;
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The export's address: a plain same-origin link, so the browser downloads
 * the workbook with the session cookie and names it from the response.
 */
export function accessCompletedExportUrl(query: AccessCompletedQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const search = params.toString();
  return `/api/access/completed/export${search === "" ? "" : `?${search}`}`;
}

/** One case. */
export function useAccessCase(caseId: string) {
  return useQuery({
    queryKey: accessKeys.detail(caseId),
    queryFn: async (): Promise<AccessCase> => {
      const { data } = await sdkGetAccessCase({ path: { case_id: caseId }, throwOnError: true });
      return data;
    },
    staleTime: 15_000,
  });
}

/**
 * The case's comments and activity (22 Oct): the public journal of its
 * anchor request in ITSM, and whether a comment can be added now.
 */
export function useAccessCaseActivity(caseId: string) {
  return useQuery({
    queryKey: accessKeys.activity(caseId),
    queryFn: async (): Promise<AccessCaseActivity> => {
      const { data } = await sdkListAccessCaseActivity({
        path: { case_id: caseId },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 15_000,
  });
}

/** "Add a comment" on the case page (22 Oct): a public note on the anchor request. */
export function useAddAccessCaseComment(caseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { body: string; body_lang: string }): Promise<TicketTimelineEntry> => {
      logger.debug("[access] comment on case", caseId);
      const { data } = await sdkAddAccessCaseComment({
        path: { case_id: caseId },
        body,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accessKeys.activity(caseId) });
    },
  });
}

/** "Open prefilled": a held item's form with its answers and the new hire. */
export function useAccessItemPrefill(caseId: string, n: number) {
  return useQuery({
    queryKey: accessKeys.itemPrefill(caseId, n),
    queryFn: async (): Promise<AccessPrefillResponse> => {
      const { data } = await sdkGetAccessItemPrefill({
        path: { case_id: caseId, n },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 0,
    retry: false,
  });
}

/** File a held item by hand; the server writes its marker so it is never filed twice. */
export function useFileAccessItem(caseId: string, n: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AccessItemSubmission): Promise<AccessFiledItem> => {
      logger.debug("[access] file held item by hand", caseId, n);
      const { data } = await sdkFileAccessItem({
        path: { case_id: caseId, n },
        body,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accessKeys.all });
    },
  });
}

/**
 * Who would approve these picks, for the company on the form (5 Oct): shown
 * live under the role pickers at "Open and complete". The last answer stays
 * on screen while the next one loads, so the list does not flicker.
 */
export function useApproversPreview(
  caseId: string,
  n: number,
  body: AccessApproversPreviewRequest,
  enabled: boolean,
) {
  return useQuery({
    queryKey: accessKeys.approvers(caseId, n, body),
    queryFn: async (): Promise<AccessApproversPreview> => {
      const { data } = await sdkPreviewAccessApprovers({
        path: { case_id: caseId, n },
        body,
        throwOnError: true,
      });
      return data;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/**
 * Approve or reject one approval (5 Oct). The server writes the decision to
 * ITSM first and answers only once it is recorded; the case is read again.
 */
export function useDecideApproval(caseId: string, n: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      needId,
      body,
    }: {
      needId: string;
      body: AccessDecisionRequest;
    }): Promise<AccessDecisionResponse> => {
      logger.debug("[access] decide approval", caseId, n, needId, body.decision);
      const { data } = await sdkDecideAccessApproval({
        path: { case_id: caseId, n, need_id: needId },
        body,
        throwOnError: true,
      });
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: accessKeys.all });
    },
  });
}

/**
 * "My approvals" (19 Oct): what waits for the signed-in approver, and what
 * they decided. Under `accessKeys.all`, so a decision refreshes it.
 */
export function useMyApprovals(enabled = true) {
  return useQuery({
    queryKey: accessKeys.approvals(),
    queryFn: async (): Promise<MyApprovalsResponse> => {
      const { data } = await sdkListMyApprovals({ throwOnError: true });
      logger.debug("[access] my approvals", data.waiting?.length ?? 0);
      return data;
    },
    enabled,
    staleTime: 15_000,
    retry: false,
  });
}

/**
 * "My approvals" in the header and the account menu (19 Oct): shown to
 * approvers only, with the count waiting. The same query and cache as the
 * page, so a decision updates the badge. Off on a shared device and while
 * the access front door is off.
 */
export function useMyApprovalsNav(): ApprovalsNav {
  const { data: me } = useMe();
  const kiosk = useKiosk();
  const query = useMyApprovals(me?.access_front_door === true && kiosk === null);
  return approvalsNav(query.data);
}

/** The step's sender has seen a rejection (19 Oct): it leaves their "Needs you". */
export function useAcknowledgeRejection(caseId: string, n: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AccessRejectionSeenResponse> => {
      logger.debug("[access] rejection seen", caseId, n);
      const { data } = await sdkAcknowledgeAccessRejection({
        path: { case_id: caseId, n },
        throwOnError: true,
      });
      return data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: accessKeys.all });
    },
  });
}
